import React, { useState, useEffect } from 'react';
import { useRequest } from 'umi';
import styles from '../css/cmapDetailsCSS.less';
import { Modal, Popover, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import CampSeamlessScroll from './campSeamlessScroll';
import cx from 'classnames';
import { StudyInfoType } from '@/types/camp/study/studentBaseInfoType';
import { CampStudyState } from '@/types/camp/study';
import DateFormat from '@/components/dateFormat';
import EntrySuccessModal from '@/pages/camp/components/EntrySuccessModal';
import { getNodeProgress } from '@/utils/requests/camp/requests_study';
import { ProgressType } from '@/types/camp/details/detailsType';
import { useLocalStorageState } from 'ahooks';
import successSvg from '@/static/images/success.svg';
import dayjs from 'dayjs';

type BannerProps = {
  studentBaseInfo: StudyInfoType | null;
  setOpen?: (open: boolean) => void;
  setEntryOpen?: (entryOpen: boolean) => void;
  setSignUpAgain?: (signUpAgain: boolean) => void;
};

const Banner: React.FC<BannerProps> = ({
  studentBaseInfo,
  setOpen,
  setEntryOpen,
  setSignUpAgain,
}) => {
  const [nodeProgress, setNodeProgress] = useState<ProgressType>();
  const [openWaiting, setOpenWaiting] = useState<boolean>(false);
  const [saveWaitingResults, setSaveWaitingResults] =
    useLocalStorageState<string[]>('modal_waiting_results');

  // 首次弹出等待公布成绩
  useEffect(() => {
    if (studentBaseInfo?.campStudyState === CampStudyState.WAITING_RESULTS) {
      if (studentBaseInfo?.batchId && !saveWaitingResults?.includes(studentBaseInfo?.batchId)) {
        setOpenWaiting(true);
        setSaveWaitingResults((prev) => [...(prev || []), studentBaseInfo?.batchId]);
      }
    }
  }, [studentBaseInfo?.batchId]);

  // 获取进度
  const { run: getNodeProgressRequest } = useRequest(
    (signUpId) => {
      return getNodeProgress(signUpId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setNodeProgress(res);
      },
    },
  );

  useEffect(() => {
    getNodeProgressRequest(studentBaseInfo?.id);
  }, []);

  // 下载证书
  const items: MenuProps['items'] = [];
  if (studentBaseInfo?.certificate?.url) {
    items.push({
      key: '1',
      label: (
        <a target="_blank" href={studentBaseInfo?.certificate?.url}>
          结营证书
        </a>
      ),
    });
  }

  if (studentBaseInfo?.certificate?.outstandingUrl) {
    items.push({
      key: '2',
      label: (
        <a target="_blank" href={studentBaseInfo?.certificate?.outstandingUrl}>
          优秀学员证书
        </a>
      ),
    });
  }

  return (
    <>
      <div className={styles.cmap_banner}>
        <div className={cx(styles.banner_warp, 'w1200 flex_3')}>
          {/* 左侧内容 */}
          <div className={cx(styles.lf, 'flex_2')}>
            <div className={styles.title}>
              {studentBaseInfo?.campStudyState === CampStudyState.CAMP_SUCCESS ||
              studentBaseInfo?.campStudyState === CampStudyState.CAMP_FAIL
                ? '挑战'
                : null}
              {studentBaseInfo?.campStudyState === CampStudyState.CAMP_SUCCESS ||
                (studentBaseInfo?.campStudyState === CampStudyState.STUDYING && '正在挑战')}
              {studentBaseInfo?.campStudyState === CampStudyState.WAITING_RESULTS && '等待'}《
              {studentBaseInfo?.camp.name}》
              {studentBaseInfo?.campStudyState === CampStudyState.CAMP_SUCCESS && '成功'}
              {studentBaseInfo?.campStudyState === CampStudyState.CAMP_FAIL && '失败'}
              {studentBaseInfo?.campStudyState === CampStudyState.WAITING_RESULTS && '公布成绩'}
            </div>
            <div className="flex_1">
              <div className="f20" style={{ marginRight: 24, marginTop: -4 }}>
                {studentBaseInfo?.batch.name}
              </div>
              {studentBaseInfo?.campStudyState === CampStudyState.STUDYING && (
                <DateFormat endTime={String(studentBaseInfo?.batch.enTime)} endText="后结束" />
              )}
              {studentBaseInfo?.campStudyState === CampStudyState.NOT_START_LEARNING && (
                <DateFormat endTime={String(studentBaseInfo?.batch.stTime)} endText="后开始" />
              )}
              {studentBaseInfo?.campStudyState === CampStudyState.WAITING_RESULTS && (
                <DateFormat
                  endTime={String(studentBaseInfo?.batch.rewardTime)}
                  endText="后公布成绩"
                />
              )}
            </div>

            {/*  打卡学习中 */}
            {studentBaseInfo?.campStudyState === CampStudyState.STUDYING && (
              <div className="flex_1">
                {studentBaseInfo?.lastStudyNode ? (
                  studentBaseInfo?.startStudy ? (
                    <a
                      className="com_camp_btn w160"
                      href={`/v2/camp/study/${studentBaseInfo?.id}?nodeid=${studentBaseInfo?.lastStudyNode.id}`}
                      style={{ marginRight: 16 }}
                    >
                      继续学习
                    </a>
                  ) : (
                    <a
                      className="com_camp_btn w160"
                      href={`/v2/camp/study/${studentBaseInfo?.id}?nodeid=${studentBaseInfo?.firstStudyNode.id}`}
                      style={{ marginRight: 16 }}
                    >
                      开始学习
                    </a>
                  )
                ) : (
                  studentBaseInfo?.firstStudyNode && (
                    <a
                      className="com_camp_btn w160"
                      href={`/v2/camp/study/${studentBaseInfo?.id}?nodeid=${studentBaseInfo?.firstStudyNode.id}`}
                      style={{ marginRight: 16 }}
                    >
                      开始学习
                    </a>
                  )
                )}
                {studentBaseInfo?.batch.qrCode && (
                  <Popover
                    content={
                      <div className="flex_4_1" style={{ gap: 16, padding: '16px 0' }}>
                        <img src={studentBaseInfo?.batch.qrCode} width={120} height={120} />
                        <div className="col808 f12">微信「扫一扫」加入学习交流群</div>
                      </div>
                    }
                    placement="bottom"
                    trigger="hover"
                    mouseEnterDelay={0.1}
                  >
                    <div className="btn_fff_def wbtn160" style={{ marginRight: 16 }}>
                      {studentBaseInfo.batch.buttonName
                        ? studentBaseInfo.batch.buttonName
                        : '专属学习交流群'}
                    </div>
                  </Popover>
                )}
                <a
                  className="col008 f16"
                  href={`/v2/camp/${studentBaseInfo?.camp.id}`}
                  style={{ marginRight: 16 }}
                >
                  打卡营介绍
                </a>
              </div>
            )}

            {/*  打卡未开始 */}
            {studentBaseInfo?.campStudyState === CampStudyState.NOT_START_LEARNING && (
              <div className="flex_1">
                {studentBaseInfo?.batch.qrCode && (
                  <Popover
                    content={
                      <div className="flex_4_1" style={{ gap: 16, padding: '16px 0' }}>
                        <img src={studentBaseInfo?.batch.qrCode} width={120} height={120} />
                        <div className="col808 f12">微信「扫一扫」加入学习交流群</div>
                      </div>
                    }
                    placement="bottom"
                    trigger="hover"
                    mouseEnterDelay={0.1}
                  >
                    <div className="com_camp_btn wbtn160" style={{ marginRight: 16 }}>
                      {studentBaseInfo.batch.buttonName
                        ? studentBaseInfo.batch.buttonName
                        : '专属学习交流群'}
                    </div>
                  </Popover>
                )}
                <a
                  className="btn_fff_def wbtn160"
                  href={`/v2/camp/${studentBaseInfo?.camp.id}`}
                  style={{ marginRight: 16 }}
                >
                  打卡营介绍
                </a>
              </div>
            )}

            {/*  等待公布成绩 */}
            {studentBaseInfo?.campStudyState === CampStudyState.WAITING_RESULTS && (
              <div className="flex_1">
                <div
                  onClick={() => setOpenWaiting(true)}
                  className="com_camp_btn w160"
                  style={{ marginRight: 16 }}
                >
                  请等待公布成绩
                </div>
                {studentBaseInfo?.batch.qrCode && (
                  <Popover
                    content={
                      <div className="flex_4_1" style={{ gap: 16, padding: '16px 0' }}>
                        <img src={studentBaseInfo?.batch.qrCode} width={120} height={120} />
                        <div className="col808 f12">微信「扫一扫」加入学习交流群</div>
                      </div>
                    }
                    placement="bottom"
                    trigger="hover"
                    mouseEnterDelay={0.1}
                  >
                    <div className="btn_fff_def wbtn160" style={{ marginRight: 16 }}>
                      {studentBaseInfo.batch.buttonName
                        ? studentBaseInfo.batch.buttonName
                        : '专属学习交流群'}
                    </div>
                  </Popover>
                )}

                <a
                  className="col008 f16"
                  href={`/v2/camp/${studentBaseInfo?.camp.id}`}
                  style={{ marginRight: 16 }}
                >
                  打卡营介绍
                </a>
              </div>
            )}

            {/*  打卡成功 */}
            {studentBaseInfo?.campStudyState === CampStudyState.CAMP_SUCCESS &&
              studentBaseInfo?.certificate && (
                <div className="flex_1">
                  <div
                    onClick={() => setOpen && setOpen(true)}
                    className="com_camp_btn w160"
                    style={{ marginRight: 16 }}
                  >
                    我的学习报告
                  </div>
                  {studentBaseInfo?.certificate.outstandingUrl ? (
                    <Dropdown menu={{ items }} placement="bottom" arrow={{ pointAtCenter: true }}>
                      <div className="btn_fff_def wbtn160" style={{ marginRight: 16 }}>
                        下载证书
                      </div>
                    </Dropdown>
                  ) : (
                    <a
                      className="btn_fff_def wbtn160"
                      target="_blank"
                      href={studentBaseInfo?.certificate.url}
                      style={{ marginRight: 16 }}
                    >
                      下载证书
                    </a>
                  )}
                </div>
              )}

            <div className="flex_1">
              {/*  打卡失败 */}
              {studentBaseInfo?.campStudyState === CampStudyState.CAMP_FAIL &&
                studentBaseInfo?.nextBatchId && (
                  <div
                    onClick={() => {
                      setSignUpAgain?.(true);
                      setEntryOpen && setEntryOpen(true);
                    }}
                    className="com_camp_btn w160"
                    style={{ marginRight: 16 }}
                  >
                    再次报名
                  </div>
                )}
              {nodeProgress &&
                studentBaseInfo?.campStudyState === CampStudyState.CAMP_FAIL &&
                nodeProgress?.allNode - nodeProgress?.successNode > 0 && (
                  <div className="col808 f14">
                    离成功就差
                    <span className="colff4 f18 " style={{ padding: '0 6px' }}>
                      {nodeProgress?.allNode - nodeProgress?.successNode}
                    </span>
                    个节点了，下次一定可以
                  </div>
                )}
            </div>
          </div>

          {/* 右侧动态滚动 */}
          <div className={cx(styles.rt)}>
            <CampSeamlessScroll batchId={studentBaseInfo?.batchId} />
          </div>
        </div>
      </div>
      <EntrySuccessModal studentBaseInfo={studentBaseInfo} />

      {/* 等待公布成绩弹窗 */}
      <Modal
        open={openWaiting}
        centered
        width={440}
        onCancel={() => {
          setOpenWaiting(false);
        }}
        footer={null}
      >
        <div style={{ textAlign: 'center' }}>
          <div>
            <img src={successSvg} width={200} />
          </div>
          <div className="f16">打卡学习结束，请等待公布成绩</div>

          <div className="col5a5 f14" style={{ paddingTop: 6 }}>
            <span>
              最终成绩和排名将在{' '}
              {dayjs(studentBaseInfo?.batch.rewardTime).format('YYYY-MM-DD HH:mm')} 公布
            </span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              className="com_camp_btn f14"
              style={{ height: 40, margin: '24px auto 10px auto', padding: '0 24px', width: 200 }}
              onClick={() => {
                setOpenWaiting(false);
              }}
            >
              我知道了
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default Banner;
